import type { DescService } from "@bufbuild/protobuf";
import { type CallOptions, type Client, Code, ConnectError, createClient, type Interceptor } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { err, ok, type Result } from "neverthrow";
import { Auth, Chat, LabelService, Tenancy } from "./gen";

declare const RUNNING_ENV: "local" | "preview" | "development" | "staging" | "production" | "test";
declare const STORYTELL_API_URL: string;

export const getBaseURL = (): Result<string, Error> => {
  if (RUNNING_ENV === "local") {
    const override = typeof window !== "undefined" && typeof STORYTELL_API_URL !== "undefined" ? STORYTELL_API_URL : "";
    return ok(override || "http://localhost:3443");
  }
  if (RUNNING_ENV === "development") {
    return ok("https://api.storytell-development.ai");
  }
  if (RUNNING_ENV === "preview") {
    return err(new Error("Preview environments are not yet implemented"));
  }
  return ok("https://api.storytell.ai");
};

type CpClientConfig = {
  getProjectId?: () => string | undefined;
};

let globalConfig: CpClientConfig = {};

export const configureCpClient = (config: CpClientConfig) => {
  globalConfig = config;
};

const projectIdInterceptor: Interceptor = (next) => async (req) => {
  const projectId = globalConfig.getProjectId?.();
  if (projectId) {
    req.header.set("X-Project-Id", projectId);
  }
  return next(req);
};

const createTransport = (): Result<ReturnType<typeof createConnectTransport>, Error> => {
  return getBaseURL().map((baseUrl) =>
    createConnectTransport({
      baseUrl,
      interceptors: [projectIdInterceptor],
    })
  );
};

// Exported only so declaration emit can name it: it has a private member and
// rides in the public Result type of every client method, so an unexported
// class here fails TS4094 even though nothing imports it directly.
export class PlatformError {
  private error: ConnectError;

  constructor(error: unknown) {
    this.error = ConnectError.from(error);
  }

  get message(): string {
    return this.error.message;
  }

  toString(): string {
    return this.message;
  }

  get code(): Code {
    return this.error.code;
  }

  get details(): unknown {
    return this.error.details;
  }

  isUnknown(): boolean {
    return this.error.code === Code.Unknown;
  }

  isNotFound(): boolean {
    return this.error.code === Code.NotFound;
  }

  isPermissionDenied(): boolean {
    return this.error.code === Code.PermissionDenied;
  }

  isInternal(): boolean {
    return this.error.code === Code.Internal;
  }

  isUnavailable(): boolean {
    return this.error.code === Code.Unavailable;
  }

  isUnauthenticated(): boolean {
    return this.error.code === Code.Unauthenticated;
  }
}

// Returns the call options based on the modifiers
export const useCallOptions = (...modifiers: Array<(options: CallOptions) => CallOptions>): CallOptions => {
  let options: CallOptions = {};

  for (const modifier of modifiers) {
    options = modifier(options);
  }

  return options;
};

// Create modifier functions
const withAbortSignal =
  (signal: AbortSignal) =>
  (options: CallOptions): CallOptions => ({
    ...options,
    signal,
  });

export const withBearerToken =
  (token?: string) =>
  (options: CallOptions): CallOptions => {
    if (!token) {
      return options;
    }
    const headers = new Headers(options.headers);
    headers.set("Authorization", `Bearer ${token}`);
    return { ...options, headers };
  };

// Create a type that wraps the methods of a service client to return Result types
// oxlint-disable-next-line typescript/no-explicit-any -- Generic constraint requires any for proper type inference
type ResultMethod<T extends (...args: any[]) => Promise<any>> = (
  ...args: Parameters<T>
) => Promise<Result<Awaited<ReturnType<T>>, PlatformError>>;

// Type that represents a client with Result-returning methods. Will never
// throw an error, but will return a Result object that contains either
// the successful result or an error.
type ResultClient<S extends DescService> = {
  // oxlint-disable-next-line typescript/no-explicit-any -- Generic constraint requires any for proper type inference
  [K in keyof Client<S>]: Client<S>[K] extends (...args: any[]) => Promise<any>
    ? ResultMethod<Client<S>[K]>
    : Client<S>[K];
};

// Build the transport lazily, on first client use, and memoize it. Doing this
// at import time would call getBaseURL() — which reads the build-time RUNNING_ENV
// define — so a surface that bundles this client transitively but never opens a
// connection (the Chief webview, which routes all I/O through its Go process)
// would crash on a missing RUNNING_ENV just by importing protobuf types.
let transportResultCache: Result<ReturnType<typeof createConnectTransport>, Error> | undefined;
const getTransport = (): Result<ReturnType<typeof createConnectTransport>, Error> => {
  if (!transportResultCache) {
    transportResultCache = createTransport();
  }
  return transportResultCache;
};

const isMethod = (member: unknown): member is (...args: unknown[]) => unknown => typeof member === "function";

const wrapMethod =
  (method: (...args: unknown[]) => unknown) =>
  async (...args: unknown[]) => {
    try {
      return ok(await method(...args));
    } catch (error) {
      return err(new PlatformError(error));
    }
  };

/**
 * Creates a client for the given service with methods that return Result objects
 */
function createResultClient<S extends DescService>(client: Client<S>): ResultClient<S> {
  const resultClient: Record<string, unknown> = {};

  for (const [key, member] of Object.entries<unknown>(client)) {
    resultClient[key] = isMethod(member) ? wrapMethod(member) : member;
  }

  return resultClient as ResultClient<S>;
}

// Cache to store created clients
// oxlint-disable-next-line typescript/no-explicit-any -- Cache stores clients of different service types
const clientCache = new Map<DescService, ResultClient<any>>();

/**
 * Get a client for the given service that returns Result objects from all methods
 */
function useClient<S extends DescService>(service: S): ResultClient<S> {
  if (clientCache.has(service)) {
    return clientCache.get(service) as ResultClient<S>;
  }

  const transportResult = getTransport();
  if (transportResult.isErr()) {
    console.error("transportResult", transportResult.error);
    throw ConnectError.from(transportResult.error);
  }

  const client = createClient(service, transportResult.value);
  const resultClient = createResultClient(client);

  clientCache.set(service, resultClient);
  return resultClient;
}

/**
 * Get a client for the AuthService.
 */
export function useAuthClient(): ResultClient<typeof Auth.AuthService> {
  return useClient(Auth.AuthService);
}

export function useTenancyClient(): ResultClient<typeof Tenancy.TenancyService> {
  return useClient(Tenancy.TenancyService);
}

export const useLabelClient = (): ResultClient<typeof LabelService.LabelService> => {
  return useClient(LabelService.LabelService);
};

/**
 * Get a client for the ChatService.
 */
export function useChatClient(): ResultClient<typeof Chat.ChatService> {
  return useClient(Chat.ChatService);
}

/**
 * Get a raw client for the ChatService for streaming. Results are
 * not wrapped in a Result Object which simplifies processing streaming
 * updates but requires the caller to handle errors.
 *
 * @internal Use useChatStreamingClient() for type-safe client with auth support
 */
function useChatStreamingClientRaw() {
  const transportResult = getTransport();
  if (transportResult.isErr()) {
    throw ConnectError.from(transportResult.error);
  }
  return createClient(Chat.ChatService, transportResult.value);
}

/**
 * Options for streaming chat client method calls, including authentication
 */
interface ChatClientOptions {
  /** Bearer token for authentication */
  bearerToken?: string;
  /** Abort signal for request cancellation */
  signal?: AbortSignal;
}

/**
 * Type-safe interface for chat client streaming methods
 */
interface StreamingChatClient {
  /**
   * Start a streaming chat with typed parameters
   * @param request - The stream request with prompt and configuration
   * @param options - Optional client configuration including auth
   * @returns Async iterable of streaming responses
   */
  stream(request: Chat.StreamRequest, options?: ChatClientOptions): AsyncIterable<Chat.StreamResponse>;

  /**
   * Start playback of a previously recorded chat stream
   * @param request - The playback request with message ID and speed
   * @param options - Optional client configuration including auth
   * @returns Async iterable of streaming playback responses
   */
  streamPlayback(
    request: Chat.StreamPlaybackRequest,
    options?: ChatClientOptions
  ): AsyncIterable<Chat.StreamPlaybackResponse>;
}

/**
 * Get a client for the ChatService for streaming with type safety and auth support.
 * Results are not wrapped in a Result Object which simplifies processing streaming
 * updates but requires the caller to handle errors.
 *
 * @example
 * ```typescript
 * const client = useChatStreamingClient();
 *
 * // Start chat with authentication
 * const stream = client.stream(
 *   newStreamRequest("", "Hello", languageRegion, projectId, workspaceId),
 *   { bearerToken: "your-auth-token" }
 * );
 *
 * // Start playback with authentication
 * const playbackStream = client.streamPlayback(
 *   newStreamPlaybackRequest("message_123", 2.0),
 *   { bearerToken: "your-auth-token" }
 * );
 * ```
 */
export function useChatStreamingClient(): StreamingChatClient {
  const client = useChatStreamingClientRaw();

  return {
    stream: (request, options) => {
      const callOptions = useCallOptions(
        ...(options?.bearerToken ? [withBearerToken(options.bearerToken)] : []),
        ...(options?.signal ? [withAbortSignal(options.signal)] : [])
      );

      return client.stream(request, callOptions);
    },

    streamPlayback: (request, options) => {
      const callOptions = useCallOptions(
        ...(options?.bearerToken ? [withBearerToken(options.bearerToken)] : []),
        ...(options?.signal ? [withAbortSignal(options.signal)] : [])
      );

      return client.streamPlayback(request, callOptions);
    },
  };
}
